import React, { Component } from "react"
import ScrollLock, { TouchScrollable } from "react-scrolllock"
import { ButtonWrapper } from "../atoms"
import { ReactComponent as Close } from "../assets/icons/modal-close.svg"
import "./Modal.css"

const FOCUSABLE_SELECTOR = [
  'a[href]',
  'button:not([disabled])',
  'input:not([disabled]):not([type="hidden"])',
  'select:not([disabled])',
  'textarea:not([disabled])',
  'iframe',
  '[tabindex]:not([tabindex="-1"])',
].join(', ')

// Shared across all Modal instances so stacked modals coordinate:
// how many open modals need an element hidden, and the aria-hidden
// value it had before the first of them hid it. An element is only
// restored when the last modal hiding it unmounts.
const ariaHiddenCounts = new WeakMap()
const ariaHiddenPriorValues = new WeakMap()

// Elements the aria-hiding walk must never touch: live regions have to
// keep announcing while a modal is open (helpers/announce.js appends one
// to document.body), and react-modal portals its dialogs to body level
// (react-modal manages its own app-hiding via setAppElement).
function isAriaHideExempt(el) {
  return (
    el.nodeName === 'SCRIPT' ||
    el.nodeName === 'STYLE' ||
    el.hasAttribute('aria-live') ||
    el.getAttribute('role') === 'status' ||
    el.getAttribute('role') === 'alert' ||
    (typeof el.className === 'string' && el.className.indexOf('ReactModal') > -1)
  )
}

class Modal extends Component {
  static defaultProps = {
    close: () => {},
    isUnclosable: false,
  }

  constructor(props) {
    super(props)
    this.state = {
      closing: false,
    }

    this.modalRef = React.createRef()
    this.previouslyFocused = null
    this._initialFocusSet = false
    this._focusFallbackTimer = null
    this.handleEsc = this.handleEsc.bind(this)
    this.handleTabTrap = this.handleTabTrap.bind(this)
  }

  componentDidMount() {
    this.previouslyFocused = document.activeElement
    window.addEventListener("keydown", this.handleEsc)
    window.addEventListener("keydown", this.handleTabTrap)

    const modalEl = this.modalRef.current
    if (modalEl) {
      this.hideOutsideContentFromAT()

      // Immediately focus the modal container to prevent any focus escape
      // during the slide-up animation. tabIndex="-1" allows programmatic
      // focus without adding it to the tab order.
      modalEl.focus({ preventScroll: true })

      // The slide-up animation is on the parent .Modal-scroll-container
      // (because TouchScrollable clones it and overwrites refs on direct
      // children). Listen for animationend on that parent.
      const scrollContainer = modalEl.parentElement
      const onAnimationEnd = () => {
        scrollContainer.removeEventListener('animationend', onAnimationEnd)
        this.setInitialFocus()
      }
      if (scrollContainer) {
        scrollContainer.addEventListener('animationend', onAnimationEnd)
      }

      // Fallback if animationend never fires (e.g., prefers-reduced-motion,
      // animation overridden, or already completed)
      this._focusFallbackTimer = setTimeout(() => {
        if (scrollContainer) {
          scrollContainer.removeEventListener('animationend', onAnimationEnd)
        }
        this.setInitialFocus()
      }, 600)
    }
  }

  componentWillUnmount() {
    window.removeEventListener("keydown", this.handleEsc)
    window.removeEventListener("keydown", this.handleTabTrap)
    if (this._focusFallbackTimer) {
      clearTimeout(this._focusFallbackTimer)
    }
    this.restoreOutsideContentToAT()
    if (this.previouslyFocused && this.previouslyFocused.focus) {
      this.previouslyFocused.focus()
    }
  }

  // aria-modal alone doesn't reliably remove the page behind the modal
  // from the accessibility tree, so mirror react-modal's ariaHideApp:
  // aria-hide every sibling of the modal's ancestor chain while open.
  // Hides are reference-counted in the shared maps above because two
  // Modals can be mounted at once (the overlay routes in index.js are
  // non-exact siblings) — an element is restored, to the aria-hidden
  // value it had before, only when the last Modal hiding it unmounts.
  hideOutsideContentFromAT() {
    this._ariaHiddenSiblings = []
    let node = this.modalRef.current
    while (node && node.parentElement && node !== document.body) {
      const parent = node.parentElement
      for (const sibling of parent.children) {
        if (sibling === node || isAriaHideExempt(sibling)) continue
        const count = ariaHiddenCounts.get(sibling) || 0
        if (count === 0) {
          ariaHiddenPriorValues.set(sibling, sibling.getAttribute('aria-hidden'))
          sibling.setAttribute('aria-hidden', 'true')
        }
        ariaHiddenCounts.set(sibling, count + 1)
        this._ariaHiddenSiblings.push(sibling)
      }
      node = parent
    }
  }

  restoreOutsideContentToAT() {
    if (!this._ariaHiddenSiblings) return
    this._ariaHiddenSiblings.forEach((el) => {
      const count = (ariaHiddenCounts.get(el) || 1) - 1
      if (count > 0) {
        ariaHiddenCounts.set(el, count)
        return
      }
      ariaHiddenCounts.delete(el)
      const prior = ariaHiddenPriorValues.get(el)
      ariaHiddenPriorValues.delete(el)
      if (prior === null || prior === undefined) {
        el.removeAttribute('aria-hidden')
      } else {
        el.setAttribute('aria-hidden', prior)
      }
    })
    this._ariaHiddenSiblings = []
  }

  setInitialFocus() {
    if (this._initialFocusSet) return
    this._initialFocusSet = true
    if (this._focusFallbackTimer) {
      clearTimeout(this._focusFallbackTimer)
      this._focusFallbackTimer = null
    }

    const modalEl = this.modalRef.current
    if (!modalEl) return

    // Allow consumer components to mark a preferred initial focus target
    const autoFocusEl = modalEl.querySelector('[data-autofocus]')
    if (autoFocusEl) {
      autoFocusEl.focus({ preventScroll: true })
      return
    }

    // Keep focus on the modal container (tabIndex="-1") so no element
    // shows a focus ring until the user presses Tab.
    modalEl.focus({ preventScroll: true })
  }

  getVisibleFocusableElements() {
    const modalEl = this.modalRef.current
    if (!modalEl) return []

    const candidates = modalEl.querySelectorAll(FOCUSABLE_SELECTOR)
    return Array.from(candidates).filter(el => {
      // Filter out aria-hidden elements and their descendants
      if (el.getAttribute('aria-hidden') === 'true') return false
      if (el.closest('[aria-hidden="true"]')) return false

      // Filter out elements not rendered in the layout.
      // offsetParent is null for display:none elements, but also for
      // position:fixed/sticky elements which ARE visible.
      if (el.offsetParent === null) {
        const style = window.getComputedStyle(el)
        if (style.display === 'none') return false
        if (style.position !== 'fixed' && style.position !== 'sticky') return false
      }

      // Filter out visibility:hidden elements
      const style = window.getComputedStyle(el)
      if (style.visibility === 'hidden') return false

      return true
    })
  }

  handleTabTrap(e) {
    if (e.key !== 'Tab' || !this.modalRef.current) return

    const modalEl = this.modalRef.current

    // If a react-modal overlay (e.g., Checkout tip dialog) is open and has
    // focus, don't interfere — let react-modal manage its own focus trap
    if (!modalEl.contains(document.activeElement) &&
        document.activeElement !== document.body &&
        document.querySelector('.ReactModal__Overlay')) {
      return
    }

    const focusable = this.getVisibleFocusableElements()

    if (focusable.length === 0) {
      // No focusable elements: keep focus on modal container
      e.preventDefault()
      return
    }

    const first = focusable[0]
    const last = focusable[focusable.length - 1]
    const active = document.activeElement

    // Helper: focus an element and ensure it's visible within the modal,
    // with enough clearance from the bottom for sticky elements like
    // the Add-to-Cart bar (~70px).
    const BOTTOM_PADDING = 80
    const focusAndScroll = (el) => {
      el.focus()
      const elRect = el.getBoundingClientRect()
      const modalRect = modalEl.getBoundingClientRect()
      const bottomOverflow = elRect.bottom - (modalRect.bottom - BOTTOM_PADDING)
      const topOverflow = modalRect.top - elRect.top
      if (bottomOverflow > 0) {
        modalEl.scrollTop += bottomOverflow
      } else if (topOverflow > 0) {
        modalEl.scrollTop -= topOverflow
      }
    }

    // Determine the next element that would receive focus
    const currentIndex = focusable.indexOf(active)

    // If focus is inside an iframe (e.g. reCAPTCHA), currentIndex will be -1
    // because activeElement is the iframe's internal element. Find which iframe
    // contains the active element so we can determine position in the list.
    let effectiveIndex = currentIndex
    if (currentIndex === -1 && modalEl.contains(active)) {
      // Check if focus is inside an iframe that's in our focusable list
      const iframeParent = active.ownerDocument !== document
        ? Array.from(modalEl.querySelectorAll('iframe')).find(f => {
            try { return f.contentDocument && f.contentDocument.contains(active) } catch(e) { return false }
          })
        : null
      if (iframeParent) {
        effectiveIndex = focusable.indexOf(iframeParent)
      }
    }

    // Determine the next target element
    let nextEl
    if (!modalEl.contains(active) || active === modalEl) {
      nextEl = e.shiftKey ? last : first
    } else if (e.shiftKey) {
      nextEl = effectiveIndex <= 0 ? last : focusable[effectiveIndex - 1]
    } else {
      nextEl = effectiveIndex >= focusable.length - 1 ? first : focusable[effectiveIndex + 1]
    }

    // If the next element is an iframe, let the browser handle Tab naturally
    // so it can focus into the iframe's internal DOM (e.g. reCAPTCHA checkbox).
    // Only intercept if we need to wrap around the edges of the modal.
    if (nextEl && nextEl.tagName === 'IFRAME') {
      if (!modalEl.contains(active) || active === modalEl) {
        // Focus coming from outside — direct it to the iframe
        e.preventDefault()
        focusAndScroll(nextEl)
      }
      // Otherwise let browser handle naturally — it knows how to enter iframes
      return
    }

    // If focus is currently inside an iframe and the next target is NOT an iframe,
    // let the browser handle Tab to exit the iframe naturally, unless we need to
    // wrap around (active is inside the last iframe and tabbing forward → wrap to first)
    if (effectiveIndex !== -1 && currentIndex === -1 && active !== modalEl) {
      // Focus is inside an iframe — let browser exit naturally
      // But if at edges, we need to wrap
      if (e.shiftKey && effectiveIndex <= 0) {
        e.preventDefault()
        focusAndScroll(last)
        return
      }
      if (!e.shiftKey && effectiveIndex >= focusable.length - 1) {
        e.preventDefault()
        focusAndScroll(first)
        return
      }
      // Let browser handle exiting the iframe naturally
      return
    }

    // For non-iframe elements, manually manage focus
    e.preventDefault()

    if (!modalEl.contains(active) || active === modalEl) {
      if (e.shiftKey) {
        focusAndScroll(last)
      } else {
        focusAndScroll(first)
      }
      return
    }

    if (e.shiftKey) {
      // Shift+Tab: go backwards
      if (effectiveIndex <= 0) {
        focusAndScroll(last)
      } else {
        focusAndScroll(focusable[effectiveIndex - 1])
      }
    } else {
      // Tab: go forwards
      if (effectiveIndex === -1 || effectiveIndex >= focusable.length - 1) {
        focusAndScroll(first)
      } else {
        focusAndScroll(focusable[effectiveIndex + 1])
      }
    }
  }

  handleEsc(e) {
    if (e.key === "Escape") {
      // Only handle if this modal owns focus. This prevents closing
      // our modal when a react-modal dialog (e.g., Checkout tip modal)
      // is open on top.
      const modalEl = this.modalRef.current
      if (modalEl && (modalEl.contains(document.activeElement) || document.activeElement === document.body)) {
        this.closeModal(e, true)
      }
    }
  }

  closeModal = (e, override) => {
    if (this.props.isUnclosable) return

    if (
      e.target.className === "Background" ||
      e.target.classList.contains("Close") ||
      override === true
    ) {
      this.setState({ closing: true }, () => {
        window.setTimeout(() => {
          this.props.close()
        }, 500)
      })
    }
  }

  render() {
    return (
      <ScrollLock>
        <div
          className="Background"
          id={this.state.closing ? "BackgroundExit" : null}
          onClick={(e) => this.closeModal(e, "background")}
          style={{...this.props.backgroundStyle}}
        >
          <TouchScrollable>
            <div
              className="Modal-scroll-container"
              id={this.state.closing ? "ModalExit" : null}
              style={{ ...this.props.containerStyle }}
            >
              <div
                ref={this.modalRef}
                className="Modal"
                role="dialog"
                aria-modal="true"
                tabIndex="-1"
                aria-labelledby={this.props.ariaLabelledBy}
                style={{ ...this.props.style }}
                onClick={null}
              >
                {!this.props.isUnclosable && !this.props.stickyX && !this.props.hideX && (
                  <ButtonWrapper className="Close" onClick={this.closeModal} aria-label="Close modal">
                    <Close aria-hidden="true" />
                  </ButtonWrapper>
                )}

                {this.props.stickyX && (
                  <div className='modal-down-X-outer-container'>
                    <ButtonWrapper
                      onClick={(e)=>this.closeModal(e,true)}
                      className='modal-down-X-container'
                      style={{position:'absolute'}}
                      aria-label="Close modal"
                    >
                      <Close aria-hidden="true" />
                    </ButtonWrapper>
                  </div>
                )}

                {this.props.children}
              </div>
            </div>
          </TouchScrollable>
        </div>
      </ScrollLock>
    )
  }
}

export default Modal
